import { findLayerById } from '../core/layerTree';
import { mat3Translation } from '../core/math';
import type {
  DocumentMeta,
  Point,
  RGBA,
  ShapeLayer,
  ShapePaint,
  ToolId,
  VectorMode,
} from '../core/types';
import {
  activePath,
  addShapeLayer,
  appendSubpathToActivePath,
  appendToShapeLayer,
  beginVectorEdit,
  cancelVectorEdit,
  commitVectorEdit,
  paintSubpathsIntoLayer,
  updatePathTransient,
  updateShapeGeometryTransient,
} from '../state/vectorActions';
import {
  geometryToSubpaths,
  hitTestPath,
  nearestKnot,
  pathToPolylines,
  pointInPath,
  transformSubpaths,
} from '../vector/geometry';
import { knot, type Knot, type ShapeGeometry, type ShapeStroke, type Subpath } from '../vector/types';
import type { OverlayContext, Tool, ToolContext, ToolPointerEvent } from './types';

// --- Overlay ---

const PATH_LINE = 'rgba(0, 0, 0, 0.9)';
const PATH_LINE_LIGHT = 'rgba(255, 255, 255, 0.9)';
const KNOT_SIZE = 3;

/** Draws a path as a two-tone hairline, so it reads over any image. */
function drawSubpaths(overlay: OverlayContext, subpaths: readonly Subpath[]): void {
  const { ctx } = overlay;
  const polylines = pathToPolylines(subpaths);
  ctx.save();
  ctx.lineWidth = 1;
  for (const pass of [PATH_LINE, PATH_LINE_LIGHT]) {
    ctx.strokeStyle = pass;
    ctx.setLineDash(pass === PATH_LINE ? [] : [4, 4]);
    for (let i = 0; i < polylines.length; i += 1) {
      const points = polylines[i];
      if (points.length < 2) continue;
      ctx.beginPath();
      const first = overlay.toScreen(points[0]);
      ctx.moveTo(first.x, first.y);
      for (let j = 1; j < points.length; j += 1) {
        const p = overlay.toScreen(points[j]);
        ctx.lineTo(p.x, p.y);
      }
      if (subpaths[i].closed) ctx.closePath();
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Anchors as squares and handles as circles on their tethers, as in Photoshop. */
function drawKnots(overlay: OverlayContext, subpaths: readonly Subpath[], handles: boolean): void {
  const { ctx } = overlay;
  ctx.save();
  ctx.lineWidth = 1;
  for (const subpath of subpaths) {
    for (const k of subpath.knots) {
      const anchor = overlay.toScreen(k.point);
      if (handles) {
        for (const offset of [k.in, k.out]) {
          if (offset.x === 0 && offset.y === 0) continue;
          const handle = overlay.toScreen({ x: k.point.x + offset.x, y: k.point.y + offset.y });
          ctx.strokeStyle = 'rgba(120, 170, 255, 0.9)';
          ctx.beginPath();
          ctx.moveTo(anchor.x, anchor.y);
          ctx.lineTo(handle.x, handle.y);
          ctx.stroke();
          ctx.fillStyle = 'rgba(120, 170, 255, 0.95)';
          ctx.beginPath();
          ctx.arc(handle.x, handle.y, KNOT_SIZE, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.fillStyle = PATH_LINE_LIGHT;
      ctx.strokeStyle = PATH_LINE;
      ctx.beginPath();
      ctx.rect(anchor.x - KNOT_SIZE, anchor.y - KNOT_SIZE, KNOT_SIZE * 2, KNOT_SIZE * 2);
      ctx.fill();
      ctx.stroke();
    }
  }
  ctx.restore();
}

// --- Shared helpers ---

function shapePaint(ctx: ToolContext): ShapePaint {
  return ctx.options.shapeFillEnabled ? { kind: 'solid', color: ctx.foreground } : { kind: 'none' };
}

function shapeStroke(ctx: ToolContext): (ShapeStroke & { color: RGBA }) | null {
  if (!ctx.options.shapeStrokeEnabled) return null;
  return {
    width: ctx.options.shapeStrokeWidth,
    align: ctx.options.shapeStrokeAlign,
    join: 'miter',
    cap: 'butt',
    dash: [],
    color: ctx.background,
  };
}

/** Stroke used when a vector tool in Pixels mode is set to outline only. */
function pixelStroke(ctx: ToolContext): (ShapeStroke & { color: RGBA }) | null {
  if (ctx.options.shapeFillEnabled) return null;
  return {
    width: Math.max(1, ctx.options.shapeStrokeWidth),
    align: 'center',
    join: 'round',
    cap: 'round',
    dash: [],
    color: ctx.foreground,
  };
}

function emitSubpaths(
  subpaths: Subpath[],
  mode: VectorMode,
  ctx: ToolContext,
  name: string,
  geometry?: ShapeGeometry,
): void {
  if (mode === 'shape') {
    // Anything but Combine cuts into the shape layer already selected.
    const active = ctx.doc.activeLayerId;
    if (ctx.options.pathOp !== 'add' && active && appendToShapeLayer(active, subpaths)) return;
    // A parametric shape is stored as such, so its parameters stay editable.
    addShapeLayer(geometry ?? { kind: 'freeform', subpaths }, shapePaint(ctx), shapeStroke(ctx), name);
    return;
  }
  if (mode === 'path') {
    for (const subpath of subpaths) appendSubpathToActivePath(subpath);
    return;
  }
  paintSubpathsIntoLayer(subpaths, pixelStroke(ctx));
}

/**
 * Sends finished geometry where the tool's mode says it belongs: a new shape
 * layer, the path being edited, or pixels in the active layer.
 */
function emitGeometry(geometry: ShapeGeometry, mode: VectorMode, ctx: ToolContext, name: string): void {
  const subpaths = geometryToSubpaths(geometry).map((subpath) => ({
    ...subpath,
    op: ctx.options.pathOp,
  }));
  emitSubpaths(subpaths, mode, ctx, name, geometry);
}

function shapeLayerAt(doc: DocumentMeta, layerId: string | null): ShapeLayer | null {
  const layer = layerId ? findLayerById(doc.layers, layerId) : null;
  return layer && layer.kind === 'shape' ? layer : null;
}

/** Tolerance in document pixels for a click that has to hit thin geometry. */
function hitTolerance(ctx: ToolContext): number {
  return 5 / ctx.view.zoom;
}

// --- Shape tools ---

interface ShapeToolSpec {
  id: ToolId;
  name: string;
  build: (start: Point, end: Point, ctx: ToolContext, fromCenter: boolean, constrain: boolean) => ShapeGeometry;
}

function normalisedBox(
  start: Point,
  end: Point,
  fromCenter: boolean,
  square: boolean,
): { x: number; y: number; width: number; height: number } {
  let dx = end.x - start.x;
  let dy = end.y - start.y;
  if (square) {
    const size = Math.max(Math.abs(dx), Math.abs(dy));
    dx = Math.sign(dx || 1) * size;
    dy = Math.sign(dy || 1) * size;
  }
  if (fromCenter) {
    return {
      x: start.x - Math.abs(dx),
      y: start.y - Math.abs(dy),
      width: Math.abs(dx) * 2,
      height: Math.abs(dy) * 2,
    };
  }
  return {
    x: Math.min(start.x, start.x + dx),
    y: Math.min(start.y, start.y + dy),
    width: Math.abs(dx),
    height: Math.abs(dy),
  };
}

const SHAPE_SPECS: ShapeToolSpec[] = [
  {
    id: 'shapeRect',
    name: 'Rectangle',
    build: (start, end, ctx, fromCenter, constrain) => ({
      kind: 'rectangle',
      ...normalisedBox(start, end, fromCenter, constrain),
      radius: ctx.options.shapeRadius,
    }),
  },
  {
    id: 'shapeEllipse',
    name: 'Ellipse',
    build: (start, end, _ctx, fromCenter, constrain) => ({
      kind: 'ellipse',
      ...normalisedBox(start, end, fromCenter, constrain),
    }),
  },
  {
    id: 'shapeLine',
    name: 'Line',
    build: (start, end, _ctx, _fromCenter, constrain) => ({
      kind: 'line',
      from: start,
      to: constrain ? constrainToAxes(start, end) : end,
    }),
  },
  {
    id: 'shapePolygon',
    name: 'Polygon',
    build: (start, end, ctx) => ({
      kind: 'polygon',
      cx: start.x,
      cy: start.y,
      radius: Math.hypot(end.x - start.x, end.y - start.y),
      sides: Math.max(3, Math.round(ctx.options.shapeSides)),
      innerRatio: ctx.options.shapeStarRatio,
      // Photoshop starts a polygon pointing at the cursor, so the drag sets the angle.
      rotation: Math.atan2(end.y - start.y, end.x - start.x),
    }),
  },
];

/** A click with no drag has nothing to draw, so its geometry is thrown away. */
function isDegenerate(geometry: ShapeGeometry): boolean {
  switch (geometry.kind) {
    case 'rectangle':
    case 'ellipse':
      return geometry.width < 0.5 || geometry.height < 0.5;
    case 'line':
      return Math.hypot(geometry.to.x - geometry.from.x, geometry.to.y - geometry.from.y) < 0.5;
    case 'polygon':
      return geometry.radius < 0.5;
    case 'freeform':
      return geometry.subpaths.every((subpath) => subpath.knots.length < 2);
  }
}

/** Shift constrains a drag to the eight directions. */
function constrainToAxes(from: Point, to: Point): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const angle = (Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * Math.PI) / 4;
  const length = Math.hypot(dx, dy);
  return { x: from.x + Math.cos(angle) * length, y: from.y + Math.sin(angle) * length };
}

function createShapeTool(spec: ShapeToolSpec): Tool {
  let start: Point | null = null;
  let current: Point | null = null;
  let geometry: ShapeGeometry | null = null;

  function update(event: ToolPointerEvent, ctx: ToolContext): void {
    if (!start) return;
    current = event.point;
    geometry = spec.build(start, current, ctx, event.alt, event.shift);
  }

  return {
    id: spec.id,
    cursor: 'crosshair',
    snaps: true,
    hint: 'Shift constrains, Alt draws from the centre',

    onPointerDown(event, ctx) {
      start = event.point;
      current = event.point;
      geometry = null;
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      update(event, ctx);
      ctx.requestRender();
    },

    onPointerUp(event, ctx) {
      update(event, ctx);
      const finished = geometry;
      start = null;
      current = null;
      geometry = null;
      if (finished && !isDegenerate(finished)) {
        emitGeometry(finished, ctx.options.vectorMode, ctx, spec.name);
      }
      ctx.requestRender();
    },

    cancel(ctx) {
      start = null;
      current = null;
      geometry = null;
      ctx.requestRender();
    },

    drawOverlay(overlay, ctx) {
      const path = activePath(ctx.doc);
      if (path) drawSubpaths(overlay, path.subpaths);
      if (geometry) drawSubpaths(overlay, geometryToSubpaths(geometry));
    },
  };
}

export const shapeRectTool = createShapeTool(SHAPE_SPECS[0]);
export const shapeEllipseTool = createShapeTool(SHAPE_SPECS[1]);
export const shapeLineTool = createShapeTool(SHAPE_SPECS[2]);
export const shapePolygonTool = createShapeTool(SHAPE_SPECS[3]);

// --- Pen ---

/** How close to the first anchor a click has to land to close the subpath. */
const CLOSE_DISTANCE = 6;

function mirrored(offset: Point): Point {
  return { x: -offset.x, y: -offset.y };
}

/**
 * The Pen tool. A click adds a corner, a drag pulls out a pair of mirrored
 * handles, Alt while dragging breaks the pair, and clicking the first anchor
 * closes the subpath. Enter finishes an open one and Backspace drops the last
 * anchor, matching Photoshop's keyboard.
 */
export const penTool: Tool = (() => {
  let knots: Knot[] = [];
  let hover: Point | null = null;
  /** Set while a handle is being pulled out of the knot just placed. */
  let dragging = false;

  function reset(): void {
    knots = [];
    hover = null;
    dragging = false;
  }

  function subpathOf(closed: boolean, op: Subpath['op']): Subpath {
    return { knots: knots.map((k) => ({ ...k })), closed, op };
  }

  function commit(closed: boolean, ctx: ToolContext): void {
    if (knots.length < 2) {
      reset();
      ctx.requestRender();
      return;
    }
    const subpath = subpathOf(closed, ctx.options.pathOp);
    reset();
    emitSubpaths([subpath], ctx.options.vectorMode, ctx, 'Shape');
    ctx.requestRender();
  }

  return {
    id: 'pen',
    cursor: 'crosshair',
    snaps: true,
    hint: 'Click for corners, drag for curves, click the first point to close',

    onPointerDown(event, ctx) {
      const first = knots[0];
      if (first && knots.length >= 2) {
        const screenDistance =
          Math.hypot(event.point.x - first.point.x, event.point.y - first.point.y) * ctx.view.zoom;
        if (screenDistance <= CLOSE_DISTANCE) {
          commit(true, ctx);
          return;
        }
      }
      knots.push(knot(event.point));
      dragging = true;
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      hover = event.point;
      if (dragging && knots.length > 0) {
        const last = knots[knots.length - 1];
        const out = { x: event.point.x - last.point.x, y: event.point.y - last.point.y };
        // Alt pulls only the outgoing handle, which is how a cusp is made.
        last.out = out;
        last.in = event.alt ? last.in : mirrored(out);
        last.smooth = !event.alt;
      }
      ctx.requestRender();
    },

    onPointerUp(_event, ctx) {
      dragging = false;
      ctx.requestRender();
    },

    onKeyDown(event, ctx) {
      if (event.key === 'Enter') {
        commit(false, ctx);
        return true;
      }
      if (event.key === 'Backspace' || event.key === 'Delete') {
        knots.pop();
        ctx.requestRender();
        return true;
      }
      if (event.key === 'Escape') {
        reset();
        ctx.requestRender();
        return true;
      }
      return false;
    },

    cancel(ctx) {
      reset();
      ctx.requestRender();
    },

    drawOverlay(overlay, ctx) {
      const path = activePath(ctx.doc);
      if (path) drawSubpaths(overlay, path.subpaths);
      if (knots.length === 0) return;

      // The rubber band shows where the next segment would go.
      const preview: Knot[] = [...knots];
      if (ctx.options.penRubberBand && hover && !dragging) {
        preview.push(knot(hover));
      }
      const subpath: Subpath = { knots: preview, closed: false, op: 'add' };
      drawSubpaths(overlay, [subpath]);
      drawKnots(overlay, [{ knots, closed: false, op: 'add' }], true);
    },
  };
})();

// --- Free Pen ---

/**
 * Ramer-Douglas-Peucker simplification. Freehand input carries one point per
 * pointer event, far more than a path needs, and the extra anchors are what make
 * a traced outline impossible to edit afterwards.
 */
function simplify(points: readonly Point[], tolerance: number): Point[] {
  if (points.length <= 2) return [...points];
  let worst = 0;
  let index = 0;
  const first = points[0];
  const last = points[points.length - 1];
  const dx = last.x - first.x;
  const dy = last.y - first.y;
  const lengthSq = dx * dx + dy * dy;

  for (let i = 1; i < points.length - 1; i += 1) {
    const p = points[i];
    const distance =
      lengthSq < 1e-12
        ? Math.hypot(p.x - first.x, p.y - first.y)
        : Math.abs((p.x - first.x) * dy - (p.y - first.y) * dx) / Math.sqrt(lengthSq);
    if (distance > worst) {
      worst = distance;
      index = i;
    }
  }

  if (worst <= tolerance) return [first, last];
  return [
    ...simplify(points.slice(0, index + 1), tolerance).slice(0, -1),
    ...simplify(points.slice(index), tolerance),
  ];
}

/**
 * Turns a polyline into a smooth path with the Catmull-Rom construction: each
 * anchor's handles point along the line between its neighbours, which is the
 * standard way to fit curves through traced points.
 */
function smoothKnots(points: readonly Point[], closed: boolean): Knot[] {
  const knots: Knot[] = points.map((point) => knot(point, true));
  const last = points.length - 1;
  for (let i = 0; i <= last; i += 1) {
    const previous = points[i === 0 ? (closed ? last : 0) : i - 1];
    const next = points[i === last ? (closed ? 0 : last) : i + 1];
    const tangent = { x: (next.x - previous.x) / 6, y: (next.y - previous.y) / 6 };
    knots[i].out = tangent;
    knots[i].in = { x: -tangent.x, y: -tangent.y };
  }
  return knots;
}

export const freePenTool: Tool = (() => {
  let points: Point[] = [];

  return {
    id: 'penFree',
    cursor: 'crosshair',
    hint: 'Draw freehand; the outline is fitted to curves',

    onPointerDown(event, ctx) {
      points = [event.point];
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (points.length === 0) return;
      const last = points[points.length - 1];
      if (Math.hypot(event.point.x - last.x, event.point.y - last.y) < 1) return;
      points.push(event.point);
      ctx.requestRender();
    },

    onPointerUp(_event, ctx) {
      const drawn = points;
      points = [];
      if (drawn.length < 3) {
        ctx.requestRender();
        return;
      }
      const tolerance = Math.max(0.5, ctx.options.penTolerance);
      const simplified = simplify(drawn, tolerance);
      const closed =
        Math.hypot(
          simplified[simplified.length - 1].x - simplified[0].x,
          simplified[simplified.length - 1].y - simplified[0].y,
        ) *
          ctx.view.zoom <=
        CLOSE_DISTANCE * 2;
      const subpath: Subpath = {
        knots: smoothKnots(closed ? simplified.slice(0, -1) : simplified, closed),
        closed,
        op: ctx.options.pathOp,
      };

      emitSubpaths([subpath], ctx.options.vectorMode, ctx, 'Shape');
      ctx.requestRender();
    },

    cancel(ctx) {
      points = [];
      ctx.requestRender();
    },

    drawOverlay(overlay, ctx) {
      const path = activePath(ctx.doc);
      if (path) drawSubpaths(overlay, path.subpaths);
      if (points.length < 2) return;
      drawSubpaths(overlay, [{ knots: points.map((p) => knot(p)), closed: false, op: 'add' }]);
    },
  };
})();

// --- Path editing ---

/** What a path tool is currently editing: a shape layer's geometry, or a path. */
type EditTarget =
  | { kind: 'shape'; layer: ShapeLayer; subpaths: Subpath[] }
  | { kind: 'path'; pathId: string; subpaths: Subpath[] };

function editTarget(ctx: ToolContext): EditTarget | null {
  const shape = shapeLayerAt(ctx.doc, ctx.doc.activeLayerId);
  if (shape) return { kind: 'shape', layer: shape, subpaths: geometryToSubpaths(shape.geometry) };
  const path = activePath(ctx.doc);
  if (path) return { kind: 'path', pathId: path.id, subpaths: path.subpaths };
  return null;
}

function writeTransient(target: EditTarget, subpaths: Subpath[]): void {
  if (target.kind === 'shape') {
    updateShapeGeometryTransient(target.layer.id, { kind: 'freeform', subpaths });
  } else {
    updatePathTransient(target.pathId, subpaths);
  }
}

/**
 * Ends a drag started with `beginEdit`. The transient write lands the final
 * geometry and the transaction turns the whole drag into one undo step.
 */
function writeCommitted(target: EditTarget, subpaths: Subpath[], label: string): void {
  writeTransient(target, subpaths);
  commitVectorEdit(label);
}

function beginEdit(target: EditTarget): void {
  beginVectorEdit(target.kind === 'shape' ? target.layer.id : null);
}

/** Translation is the one edit a parametric shape survives, so it stays parametric. */
function translateGeometry(geometry: ShapeGeometry, dx: number, dy: number): ShapeGeometry {
  switch (geometry.kind) {
    case 'rectangle':
      return { ...geometry, x: geometry.x + dx, y: geometry.y + dy };
    case 'ellipse':
      return { ...geometry, x: geometry.x + dx, y: geometry.y + dy };
    case 'line':
      return {
        ...geometry,
        from: { x: geometry.from.x + dx, y: geometry.from.y + dy },
        to: { x: geometry.to.x + dx, y: geometry.to.y + dy },
      };
    case 'polygon':
      return { ...geometry, cx: geometry.cx + dx, cy: geometry.cy + dy };
    case 'freeform':
      return { kind: 'freeform', subpaths: transformSubpaths(geometry.subpaths, mat3Translation(dx, dy)) };
  }
}

function translate(target: EditTarget, dx: number, dy: number): void {
  if (target.kind === 'shape') {
    updateShapeGeometryTransient(target.layer.id, translateGeometry(target.layer.geometry, dx, dy));
  } else {
    writeTransient(target, transformSubpaths(target.subpaths, mat3Translation(dx, dy)));
  }
}

/**
 * Path Selection: drags a whole path. Editing a parametric shape turns it into a
 * freeform one, since moved knots can no longer be described by the parameters.
 */
export const pathSelectTool: Tool = (() => {
  let target: EditTarget | null = null;
  let origin: Point | null = null;
  let moved = false;

  return {
    id: 'pathSelect',
    cursor: 'default',
    hint: 'Drag a path to move it whole',

    onPointerDown(event, ctx) {
      const candidate = editTarget(ctx);
      // Clicking the filled interior grabs the path too, not just its outline.
      const hit =
        candidate !== null &&
        (hitTestPath(candidate.subpaths, event.point, hitTolerance(ctx) * 2) ||
          pointInPath(candidate.subpaths, event.point));
      if (!candidate || !hit) {
        target = null;
        return;
      }
      target = candidate;
      origin = event.point;
      moved = false;
      beginEdit(candidate);
    },

    onPointerMove(event, ctx) {
      if (!target || !origin) return;
      const dx = event.point.x - origin.x;
      const dy = event.point.y - origin.y;
      if (!moved && Math.hypot(dx, dy) < 0.5) return;
      moved = true;
      translate(target, dx, dy);
      ctx.requestRender();
    },

    onPointerUp(event, ctx) {
      if (target && origin && moved) {
        translate(target, event.point.x - origin.x, event.point.y - origin.y);
        commitVectorEdit('Move Path');
      } else {
        cancelVectorEdit();
      }
      target = null;
      origin = null;
      moved = false;
      ctx.requestRender();
    },

    cancel() {
      cancelVectorEdit();
      target = null;
      origin = null;
      moved = false;
    },

    drawOverlay(overlay, ctx) {
      const current = target ?? editTarget(ctx);
      if (!current) return;
      drawSubpaths(overlay, current.subpaths);
      drawKnots(overlay, current.subpaths, false);
    },
  };
})();

/** Direct Selection: drags one anchor or one handle. */
export const directSelectTool: Tool = (() => {
  let target: EditTarget | null = null;
  let grabbed: { subpath: number; knot: number; handle: 'point' | 'in' | 'out' } | null = null;
  let working: Subpath[] = [];

  function apply(point: Point): Subpath[] {
    const hit = grabbed;
    if (!hit) return working;
    return working.map((subpath, subpathIndex) => {
      if (subpathIndex !== hit.subpath) return subpath;
      return {
        ...subpath,
        knots: subpath.knots.map((k, knotIndex) => {
          if (knotIndex !== hit.knot) return k;
          if (hit.handle === 'point') {
            return { ...k, point };
          }
          const offset = { x: point.x - k.point.x, y: point.y - k.point.y };
          // A smooth knot keeps its handles opposite, so dragging one turns both.
          if (hit.handle === 'out') {
            return { ...k, out: offset, in: k.smooth ? mirrored(offset) : k.in };
          }
          return { ...k, in: offset, out: k.smooth ? mirrored(offset) : k.out };
        }),
      };
    });
  }

  return {
    id: 'directSelect',
    cursor: 'default',
    hint: 'Drag anchors and handles; Alt+click an anchor to make it a corner',

    onPointerDown(event, ctx) {
      const candidate = editTarget(ctx);
      if (!candidate) return;
      const hit = nearestKnot(candidate.subpaths, event.point, hitTolerance(ctx) * 2);
      if (!hit) return;
      target = candidate;
      working = candidate.subpaths;
      grabbed = hit;
      beginEdit(candidate);

      if (event.alt && hit.handle === 'point') {
        // Alt+click strips a knot's handles, the usual way to make a corner.
        working = working.map((subpath, subpathIndex) =>
          subpathIndex !== hit.subpath
            ? subpath
            : {
                ...subpath,
                knots: subpath.knots.map((k, knotIndex) =>
                  knotIndex === hit.knot
                    ? { ...k, in: { x: 0, y: 0 }, out: { x: 0, y: 0 }, smooth: false }
                    : k,
                ),
              },
        );
        grabbed = null;
        writeCommitted(target, working, 'Convert Point');
        target = null;
        ctx.requestRender();
        return;
      }
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (!target || !grabbed) return;
      writeTransient(target, apply(event.point));
      ctx.requestRender();
    },

    onPointerUp(event, ctx) {
      if (target && grabbed) writeCommitted(target, apply(event.point), 'Edit Path');
      else cancelVectorEdit();
      target = null;
      grabbed = null;
      working = [];
      ctx.requestRender();
    },

    cancel() {
      cancelVectorEdit();
      target = null;
      grabbed = null;
      working = [];
    },

    drawOverlay(overlay, ctx) {
      const current = target ?? editTarget(ctx);
      if (!current) return;
      drawSubpaths(overlay, current.subpaths);
      drawKnots(overlay, current.subpaths, true);
    },
  };
})();
